// ─────────────────────────────────────────────────────────────────
// Ideation AV — Gallery projects seed data (exact copy supplied by client).
// Will be ported to Supabase `gallery_projects` in Phase 8 (admin panel).
// ─────────────────────────────────────────────────────────────────

import { U } from "./services";

export interface ProjectDef {
  slug: string;
  title: string;
  category: string;
  categoryKey: string;
  hero: string;
  paragraph1: string;
  paragraph2: string;
  photos: string[];
  youtube?: string;
}

// 12 vibrant Unsplash photos used as a rotating pool until real photos are uploaded.
const PHOTO_POOL = [
  U("1429962714451-bb934ecdc4ec"),
  U("1470229722913-7c0e2dbbafd3"),
  U("1516450360452-9312f5e86fc7"),
  U("1514525253161-7a46d19cd819"),
  U("1505373877841-8d25f7d46678"),
  U("1492684223066-81342ee5ff30"),
  U("1511578314322-379afb476865"),
  U("1519167758481-83f550bb49b3"),
  U("1464366400600-7168b8af9bc3"),
  U("1493225457124-a3eb161ffa5f"),
  U("1465847899084-d164df4dedc6"),
  U("1574691250077-03a929faece5"),
];
const pick = (start: number, count = 6) =>
  Array.from({ length: count }, (_, i) => PHOTO_POOL[(start + i) % PHOTO_POOL.length]);

export const PROJECTS: ProjectDef[] = [
  {
    slug: "annual-conference-2024",
    title: "Annual Conference 2024",
    category: "Corporate Events",
    categoryKey: "corporate",
    hero: U("1511578314322-379afb476865", 2400, 90),
    paragraph1:
      "A flagship annual conference for one of the region's leading financial institutions, bringing together over 1,500 delegates from across the Middle East and South Asia. Ideation delivered complete technical production encompassing a fully custom-built main stage, LED backdrop, precision FOH audio and sophisticated conference lighting.",
    paragraph2:
      "The event required seamless integration of live presentation graphics, multi-camera video switching and simultaneous translation systems — all coordinated by our technical team in real time, delivering a polished, professional production that reflected the stature of the client.",
    photos: pick(0),
  },
  {
    slug: "rock-night-festival",
    title: "Rock Night Festival Stage",
    category: "Live Concerts",
    categoryKey: "concerts",
    hero: U("1429962714451-bb934ecdc4ec", 2400, 90),
    paragraph1:
      "A high-energy live concert production for one of the region's most anticipated music events. Ideation was responsible for full audio design and deployment — deploying a large-format line array system covering the entire arena floor and upper tiers with zero dead zones.",
    paragraph2:
      "The lighting rig featured over 200 moving head fixtures, automated follow spots and a full laser system synchronised to the music. SFX included CO2 jets, confetti and atmospheric haze — delivering a concert experience that matched international touring production standards.",
    photos: pick(1),
    youtube: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  },
  {
    slug: "national-day-gala",
    title: "National Day Gala",
    category: "Government & Gala",
    categoryKey: "gala",
    hero: U("1519167758481-83f550bb49b3", 2400, 90),
    paragraph1:
      "A grand national celebration honouring UAE National Day, attended by senior government officials and dignitaries. Ideation delivered full end-to-end technical production — from initial concept and 3D visualisation through to live event execution at one of Dubai's most prestigious venues.",
    paragraph2:
      "The centrepiece was a large-format projection mapping show across a custom-built architectural stage structure, synchronised with live orchestral performance, laser displays and precision pyrotechnics. Every element was time-coded and rehearsed to the second for a flawless live broadcast.",
    photos: pick(2),
  },
  {
    slug: "tech-expo-stand",
    title: "Tech Expo Stand",
    category: "Exhibitions",
    categoryKey: "exhibitions",
    hero: U("1505373877841-8d25f7d46678", 2400, 90),
    paragraph1:
      "A premium exhibition stand production for a global technology brand at one of the region's largest trade shows. Ideation provided full technical design, supply and operation — including high-resolution LED walls, architectural lighting and integrated AV presentation systems.",
    paragraph2:
      "The stand was designed to immerse visitors in the brand's product ecosystem. Custom LED panels formed the backdrop for live product demonstrations while precision lighting highlighted key display areas. Our 3D visualisation service was used to align the client on the final look before build day.",
    photos: pick(3),
  },
  {
    slug: "festival-stage-open-air",
    title: "Festival Stage Open Air",
    category: "Live Concerts",
    categoryKey: "concerts",
    hero: U("1516450360452-9312f5e86fc7", 2400, 90),
    paragraph1:
      "An open-air festival production for one of Dubai's most popular annual entertainment events. Ideation designed and built a full ground-support stage system, accommodating multiple artists across a two-day programme with complete audio and lighting infrastructure.",
    paragraph2:
      "The outdoor environment presented unique acoustic challenges that our engineers solved with a carefully designed delay system and directional speaker array. The lighting rig was weather-proofed and programmed to deliver visually stunning shows across both evenings of the festival.",
    photos: pick(4),
  },
  {
    slug: "gala-dinner-production",
    title: "Gala Dinner Production",
    category: "Social Events",
    categoryKey: "social",
    hero: U("1464366400600-7168b8af9bc3", 2400, 90),
    paragraph1:
      "An exclusive gala dinner production for a high-profile private client at one of Dubai's most prestigious venues. Ideation transformed the ballroom with a bespoke lighting scheme, custom LED centrepiece structures and immersive atmospheric effects.",
    paragraph2:
      "The brief called for elegance and intimacy. Every cue was programmed to complement the evening's programme — from cocktail reception through to live entertainment and dinner service. The result was a seamless, sophisticated experience that exceeded the client's expectations.",
    photos: pick(5),
  },
  {
    slug: "product-launch-difc",
    title: "Product Launch DIFC",
    category: "Corporate Events",
    categoryKey: "corporate",
    hero: U("1511578314322-379afb476865", 2400, 90),
    paragraph1:
      "A stunning product launch event for a luxury automotive brand held at an exclusive venue in Dubai's financial district. Ideation was commissioned to design and deliver an immersive reveal experience — with the vehicle as the centrepiece of a full projection mapping show.",
    paragraph2:
      "The reveal sequence was crafted with our 3D visualisation team months in advance, allowing the client to approve every frame before production began. On the night, precision lighting, projection and audio cues were triggered in perfect synchronisation for a reveal moment that left the audience breathless.",
    photos: pick(6),
  },
  {
    slug: "awards-ceremony",
    title: "Awards Ceremony",
    category: "Government & Gala",
    categoryKey: "gala",
    hero: U("1492684223066-81342ee5ff30", 2400, 90),
    paragraph1:
      "A prestigious awards ceremony honouring excellence across multiple sectors, attended by government ministers, CEOs and industry leaders. Ideation provided complete technical production — from stage design and rigging through to audio, lighting and LED systems.",
    paragraph2:
      "The ceremony format required seamless transitions between award presentations, live performances and video playback segments. Our production team coordinated every technical element in real time, ensuring a smooth, broadcast-quality show from start to finish.",
    photos: pick(7),
  },
  {
    slug: "brand-exhibition-gitex",
    title: "Brand Exhibition GITEX",
    category: "Exhibitions",
    categoryKey: "exhibitions",
    hero: U("1505373877841-8d25f7d46678", 2400, 90),
    paragraph1:
      "A multi-stand exhibition production for a collective of emerging technology companies at GITEX — one of the world's largest technology events. Ideation was appointed as sole technical production partner across all participating brands.",
    paragraph2:
      "The project required meticulous coordination across multiple stand locations within the same hall — with shared power and rigging infrastructure. Each stand had its own unique LED and lighting specification, all designed and installed by our team within a tight overnight build window.",
    photos: pick(8),
  },
  {
    slug: "lights-and-sound-show",
    title: "Lights & Sound Show",
    category: "Live Concerts",
    categoryKey: "concerts",
    hero: U("1493225457124-a3eb161ffa5f", 2400, 90),
    paragraph1:
      "A spectacular outdoor lights and sound show commissioned as part of Dubai's annual entertainment calendar. Ideation delivered a fully original production combining live audio, laser mapping, pyrotechnics and projection to create a multi-sensory spectacle for audiences of up to 8,000 per show.",
    paragraph2:
      "The show ran on a 22-minute time-coded sequence repeated across multiple evenings. Every technical element was engineered for repeatability and reliability — essential for a show of this format and frequency, running night after night without a single technical interruption.",
    photos: pick(9),
  },
  {
    slug: "town-hall-2000-pax",
    title: "Town Hall 2000 Pax",
    category: "Corporate Events",
    categoryKey: "corporate",
    hero: U("1514525253161-7a46d19cd819", 2400, 90),
    paragraph1:
      "A large-scale town hall event for a multinational corporation, bringing together 2,000 employees from global offices simultaneously — live-streamed to remote offices across 14 countries, requiring broadcast-grade audio, video and switching infrastructure.",
    paragraph2:
      "Ideation designed a custom audio system to cover the venue's unique acoustic footprint, while our video team managed multi-camera capture, real-time graphics and streaming encoding. The event ran for six hours with zero technical interruptions — a testament to our team's preparation and expertise.",
    photos: pick(10),
  },
  {
    slug: "private-celebration",
    title: "Private Celebration",
    category: "Social Events",
    categoryKey: "social",
    hero: U("1464366400600-7168b8af9bc3", 2400, 90),
    paragraph1:
      "An intimate yet spectacular private celebration for a high-net-worth client in Dubai. Ideation was briefed to transform a private venue into an immersive event environment — complete with custom lighting design, live entertainment audio and a choreographed SFX finale.",
    paragraph2:
      "Discretion and precision were equally important on this project. Our team completed the full build and strike within tight access windows while delivering production quality equal to any large-scale public event. The client described it as the best private event they had ever hosted.",
    photos: pick(11),
  },
];

export const getProjectBySlug = (slug?: string) => PROJECTS.find((p) => p.slug === slug);

export const CATEGORIES = [
  { label: "All", key: "all" },
  { label: "Corporate Events", key: "corporate" },
  { label: "Live Concerts", key: "concerts" },
  { label: "Government & Gala", key: "gala" },
  { label: "Exhibitions", key: "exhibitions" },
  { label: "Social Events", key: "social" },
];
